import classNames from "classnames";
import { Icon, Title } from "components/commons";
import { ModuleWrapper } from "components/fragments";
import { StaffContext, VenueContext } from "contexts";
import { useMount, useRouter } from "hooks";
import { Path } from "paths";
import React, { useContext } from "react";
import lang from "translations";
import { TrackEvent, mixpanel } from "mixpanel";
import { useFlags } from "launchdarkly-react-client-sdk";
import styles from "./setting-nav-page.module.scss";

const settingIcons = {
  gear: (
    <><line x1="4" y1="8" x2="20" y2="8" /><line x1="4" y1="16" x2="20" y2="16" /><circle cx="9" cy="8" r="2.3" /><circle cx="15" cy="16" r="2.3" /></>
  ),
  "nfc-tag1": (
    <><path d="M8 5H6a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h2" /><path d="M16 5h2a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1h-2" /><circle cx="10.5" cy="12" r="1" /><path d="M13 10a3 3 0 0 1 0 4" /><path d="M15 8.5a5.5 5.5 0 0 1 0 7" /></>
  ),
  receipt: (
    <><path d="M6 3h12v18l-2.4-1.5L13.2 21l-2.6-1.5L8 21l-2-1.5z" /><path d="M9.5 8h5" /><path d="M9.5 12h5" /></>
  ),
  questions: (
    <><path d="M6 3h8l5 5v12a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1z" /><path d="M14 3v5h5" /><path d="M9.7 12a2.3 2.3 0 1 1 3.2 2.1c-.7.3-.9.8-.9 1.5" /><path d="M12 18h.01" /></>
  ),
  "account-setting": (
    <><path d="M12 3l7 3v5c0 4.5-3 8-7 10-4-2-7-5.5-7-10V6z" /><path d="M9 12l2 2 4-4" /></>
  ),
  integration: (
    <><rect x="4" y="4" width="7" height="7" rx="1.6" /><rect x="13" y="4" width="7" height="7" rx="1.6" /><rect x="4" y="13" width="7" height="7" rx="1.6" /><path d="M16.5 14v5" /><path d="M14 16.5h5" /></>
  ),
  "credit-card": (
    <><rect x="3" y="6" width="18" height="12" rx="2.5" /><path d="M3 10h18" /><path d="M7 14h4" /></>
  ),
  taxes: (
    <><path d="M6 3h12v18l-2.4-1.5L13.2 21l-2.6-1.5L8 21l-2-1.5z" /><path d="M9 14.5l6-6" /><circle cx="9.6" cy="9.6" r="1" /><circle cx="14.4" cy="14.4" r="1" /></>
  ),
  bill: (
    <><path d="M3 12.5V5a2 2 0 0 1 2-2h7.5a2 2 0 0 1 1.4.6l6.5 6.5a2 2 0 0 1 0 2.8l-7.6 7.6a2 2 0 0 1-2.8 0L3.6 13.9A2 2 0 0 1 3 12.5z" /><circle cx="8" cy="8" r="1.3" /></>
  ),
  tip: (
    <><circle cx="12" cy="12" r="8" /><path d="M14.6 9.3A3 3 0 0 0 12 8c-1.7 0-3 1-3 2.2 0 2.9 6 1.4 6 4.4 0 1.2-1.3 2.2-3 2.2a3 3 0 0 1-2.6-1.3" /><path d="M12 6.4v11.2" /></>
  ),
  "account-settings": (
    <><circle cx="12" cy="12" r="9" /><circle cx="12" cy="10" r="3" /><path d="M6.5 18.6a5.6 5.6 0 0 1 11 0" /></>
  ),
};

const SettingIcon = ({ name, fontSize }) => {
  if (!settingIcons[name]) {
    return <Icon name={name} className={styles.icon} fontSize={fontSize} />;
  }

  return (
    <svg className={styles.svgIcon} viewBox="0 0 24 24" aria-hidden="true">
      {settingIcons[name]}
    </svg>
  );
};

const SettingNavPage = () => {
  const { history } = useRouter();
  const { venue } = useContext(VenueContext);
  const { hasAccessOnRoute } = useContext(StaffContext);
  const { integrationsSetting } = useFlags();

  useMount(() => {
    mixpanel.track(TrackEvent.VisitedPage, {
      page: lang.settings,
    });
  });

  const pages = [
    {
      title: lang.venueSettings,
      items: [
        {
          title: lang.general,
          description: lang.yourGeneralSettings,
          icon: "gear",
          path: Path.SETTING_GENERAL_SETTING,
          fontSize: "30px",
        },
        // {
        //   title: lang.notifications,
        //   description: lang.manageNotifcations,
        //   icon: "notification",
        //   path: Path.SETTING_NOTIFICATION,
        //   fontSize: "30px",
        // },
        // {
        //   title: lang.banTags,
        //   description: lang.restrictUseOfTags,
        //   icon: "cancel",
        //   path: Path.BAN_TAG,
        //   fontSize: "26px",
        // },
        {
          title: lang.wristbandKeeping,
          description: lang.allowOrRestrictGuestWBKeeping,
          icon: "nfc-tag1",
          path: Path.WRISTBAND_KEEPING,
          fontSize: "22px",
        },
        {
          title: lang.receipts,
          description: lang.manageReceipts,
          icon: "receipt",
          path: Path.RECEIPT_SETTING,
          fontSize: "26px",
        },
        {
          title: lang.questions,
          description: lang.guestInformationYouCollect,
          icon: "questions",
          path: Path.QUESTION_SETTING,
          fontSize: "30px",
        },
        {
          title: lang.accessControl,
          description: lang.manageOfPermissionToEnterSpecificAreas,
          icon: "account-setting",
          path: Path.SETTING_ACCESS,
          fontSize: "30px",
        },        
        {
          title: lang.integrations,
          description: lang.integrationSettingDesc,
          icon: "integration",
          path: Path.INTEGRATION_SETTING,
          fontSize: "26px",
          featureFlag: integrationsSetting,
        },
      ],
    },
    {
      title: lang.paymentAndCreditSettings,
      items: [
        {
          title: lang.chartOfAccounts,
          description: lang.chartOfAccountsDescription,
          icon: "chart-of-accounts",
          path: Path.CHART_OF_ACCOUNTS,
          fontSize: "30px",
          hidden: true,
        },
        {
          title: lang.accountMapping,
          description: lang.accountMappingDescription,
          icon: "account-mapping",
          path: Path.ACCOUNT_MAPPING,
          fontSize: "30px",
          hidden: true,
        },
        {
          title: lang.paymentMethods,
          description: lang.paymentMethodsYouAccept,
          icon: "credit-card",
          path: Path.PAYMENT_METHOD_SETTING,
          fontSize: "30px",
        },
        {
          title: lang.taxes,
          description: lang.manageYourVenueTax,
          icon: "taxes",
          path: Path.SETTING_TAX,
          fontSize: "30px",
        },
        /*
        {
          title: lang.credits,
          description: lang.allowPostpaidAndLimit,
          icon: "credits",
          path: Path.CREDIT_LIMIT_SETTING,
          fontSize: "30px",
        },
        */
        {
          title: lang.feesAndSurcharges,
          trackingTitle: lang.additionalCharges,
          description: lang.addAndManageAdditionalCharge,
          icon: "bill",
          path: venue.integratedSiigo ? null : Path.SETTING_ADDITIONAL_CHARGE,
          fontSize: "30px",
          disabled: venue.integratedSiigo ? true : false,
        },
        {
          title: lang.tip,
          description: lang.allowAndSetTip,
          icon: "tip",
          path: Path.SETTING_TIP,
          fontSize: "30px",
        },
      ],
    },
    // {
    //   title: lang.checkInSettings,
    //   items: [
    //     {
    //       title: lang.questions,
    //       description: lang.guestInformationYouCollect,
    //       icon: "questions",
    //       path: Path.QUESTION_SETTING,
    //       fontSize: "30px",
    //     },
    //     {
    //       title: lang.accessControl,
    //       description: lang.manageOfPermissionToEnterSpecificAreas,
    //       icon: "account-setting",
    //       path: Path.SETTING_ACCESS,
    //       fontSize: "30px",
    //     },
    //   ],
    // },
    {
      title: lang.accountSettings,
      items: [
        {
          title: lang.account,
          description: lang.editYourAccountPreferences,
          icon: "account-settings",
          path: Path.ACCOUNT_SETTING,
          fontSize: "30px",
        },
        // {
        //   title: lang.payments,
        //   description: lang.setupYourPaymentGateways,
        //   icon: "card1",
        //   path: Path.PAYMENT_GATEWAY_SETTING,
        //   fontSize: "30px",
        // },
      ],
    },
  ]
    .filter((panel) => {
      return panel.items.filter((item) => {
        return hasAccessOnRoute(item.path);
      }).length;
    })
    .map((panel) => {
      return {
        ...panel,
        items: panel.items.filter((item) => {
          return hasAccessOnRoute(item.path);
        }),
      };
    });
  return (
    <ModuleWrapper>
      <div className={styles.settingsPage}>
        <h1 className={styles.pageTitle}>{lang.settings}</h1>

        {pages.map((page) => {
          return (
            <section className={styles.section} key={page.title}>
              <Title className={styles.sectionTitle}>{page.title}</Title>
              <div className={styles.settingsGrid}>
                {page.items.map((item) => {
                  if (item.hidden || (item.featureFlag !== undefined && !item.featureFlag)) {
                    return null;
                  }
                  return (
                      <a
                        key={item.title}
                        href={item.path || undefined}
                        className={classNames(styles.settingsTile, {
                          [styles.disabled]: item.disabled,
                        })}
                        onClick={(event) => {
                          event.preventDefault();
                          mixpanel.track(TrackEvent.ClickedButton, {
                            button: item.trackingTitle || item.title,
                          });

                          if (!item.disabled && item.path) {
                            history.push(item.path);
                          }
                        }}
                      >
                        <div className={styles.iconBox}>
                          <SettingIcon name={item.icon} fontSize={item.fontSize} />
                        </div>
                        <div className={styles.tileContent}>
                          <Title className={styles.tileTitle} md>
                            {item.title}
                          </Title>
                        </div>
                        <span className={styles.chevron} aria-hidden="true" />
                      </a>
                    );
                })}
              </div>
            </section>
          );
        })}
      </div>
    </ModuleWrapper>
  );
};

export default SettingNavPage;
